import axios from "axios";
import { toast } from "react-hot-toast";
import { createTrayStore } from "../utilities/createTrayStore";

// Uploading a PDF to redact, then following the server while it identifies PII.
// Runs outside React (see createTrayStore) so it continues, and the tray keeps
// showing it, when the officer leaves the Redactions page.

// The Redactions list listens for this to refresh.
export const REDACTIONS_CHANGED_EVENT = "pnai:redactions-changed";

export type RedactionUploadItem = {
  id: string;
  name: string;
  // "processing": uploaded; the server is identifying PII.
  status: "uploading" | "processing" | "done" | "error";
  progress: number;
  after: string;
  error?: string;
  // Set once the document exists; where the tray's name link goes.
  openPath?: string;
};

const store = createTrayStore<RedactionUploadItem>(["uploading", "processing"]);
const patch = store.patch;

export const subscribeRedactionUploads = store.subscribe;
export const getRedactionUploads = store.get;
export const clearFinishedRedactionUploads = store.clearFinished;

const controllers = new Map<string, AbortController>();
// Only the upload itself needs this tab; once the document is created the
// server carries on, so warn about leaving only until then.
const blocking = new Set<string>();
const warnBeforeLeaving = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  e.returnValue = "";
  return "";
};
const setBlocking = (id: string, on: boolean) => {
  if (on) blocking.add(id);
  else blocking.delete(id);
  window.removeEventListener("beforeunload", warnBeforeLeaving);
  if (blocking.size) window.addEventListener("beforeunload", warnBeforeLeaving);
};

// Tests only.
export function resetRedactionUploadsForTests() {
  store.reset();
  controllers.forEach((c) => c.abort());
  controllers.clear();
  blocking.clear();
}

// The X on an uploading file. The document row is only created after the bytes
// land, so stopping leaves nothing behind.
export function cancelRedactionUpload(id: string): void {
  const item = store.get().find((i) => i.id === id);
  if (!item || item.status !== "uploading") return;
  controllers.get(id)?.abort();
  setBlocking(id, false);
  store.remove(id);
}

// Exported so tests can shorten the wait between status checks.
export const redactionUploadTiming = { pollMs: 4000, timeoutMs: 20 * 60 * 1000 };

export interface StartRedactionUploadParams {
  file: File;
  name: string;
  pageCount: number;
  instructions?: string;
}

export function startRedactionUpload(params: StartRedactionUploadParams): string {
  const id = `redaction-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const controller = new AbortController();
  controllers.set(id, controller);
  store.add({ id, name: params.name, status: "uploading", progress: 0, after: "" });
  setBlocking(id, true);
  void run(id, params, controller);
  return id;
}

async function run(id: string, p: StartRedactionUploadParams, controller: AbortController) {
  let docId: string | undefined;
  try {
    const { data: presign } = await axios.post("/api/redaction-documents/get-upload-url", {
      fileName: p.file.name,
      fileType: p.file.type,
    });
    await axios.put(presign.uploadURL, p.file, {
      headers: { "Content-Type": p.file.type },
      signal: controller.signal,
      onUploadProgress: (e) => patch(id, { progress: e.total ? Math.round((e.loaded * 100) / e.total) : 0 }),
    });
    controllers.delete(id);

    patch(id, { status: "processing", progress: 100, after: "creating document" });
    const { data: doc } = await axios.post("/api/redaction-documents", {
      name: p.name,
      original_pdf_url: presign.url,
      original_pdf_key: presign.key,
      page_count: p.pageCount,
      redaction_instructions: (p.instructions || "").trim(),
    });
    docId = doc.id;
    window.dispatchEvent(new Event(REDACTIONS_CHANGED_EVENT));
    setBlocking(id, false); // from here the server does the work

    patch(id, { after: "identifying PII" });
    await axios.post(`/api/redaction-documents/${doc.id}/process`);
    window.dispatchEvent(new Event(REDACTIONS_CHANGED_EVENT));

    // Follow the server until it is ready to review (or has failed).
    const deadline = Date.now() + redactionUploadTiming.timeoutMs;
    for (;;) {
      if (Date.now() > deadline) throw new Error("Redaction took too long");
      await new Promise((r) => setTimeout(r, redactionUploadTiming.pollMs));
      const { data: latest } = await axios.get(`/api/redaction-documents/${doc.id}`);
      if (latest.status === "ready") {
        patch(id, { status: "done", after: "ready to review", openPath: `/redactions/${doc.id}` });
        toast.success("Document redacted. Ready to review.");
        return;
      }
      if (latest.status === "error") {
        throw new Error(latest.error || "Automatic redaction failed");
      }
    }
  } catch (error: any) {
    // Stopped by the officer: cancelRedactionUpload already removed the item.
    if (controller.signal.aborted) return;
    console.error(`Redaction upload failed for ${p.name}:`, error);
    const reason = error?.response?.data?.error || error?.message || "Upload failed";
    patch(id, {
      status: "error",
      error: docId ? `${reason}. The document is in your list; open it to redact by hand.` : reason,
      // The document exists once created, so the officer can still open it.
      openPath: docId ? `/redactions/${docId}` : undefined,
    });
    toast.error(docId ? "Automatic redaction failed" : "Failed to upload document");
  } finally {
    controllers.delete(id);
    setBlocking(id, false);
    window.dispatchEvent(new Event(REDACTIONS_CHANGED_EVENT));
  }
}
